/**
 * iter_196 — Community Moderation Modal.
 *
 * One modal, three modes:
 *   • archive           → soft-delete (preserves posts/members). Reversible.
 *   • restore           → un-archive
 *   • delete_permanent  → hard-delete. Requires typing the exact community
 *                         name to authorize. Cannot be undone.
 *
 * Rendered inside the Crew detail management block AND the Admin
 * Community Control Center. Only shown to authorized viewers — the
 * caller decides who sees the trigger.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const MODES = {
  archive: {
    title: "Archive community",
    body: "Archiving hides this community from public listings. Posts, comments, and members are preserved and everything can be restored later.",
    cta: "Archive",
    ctaClass: "bg-amber-500 hover:bg-amber-600 text-black",
    requireName: false,
  },
  restore: {
    title: "Restore community",
    body: "This will make the community visible again and re-enable joining. Nothing else changes.",
    cta: "Restore",
    ctaClass: "bg-emerald-500 hover:bg-emerald-600 text-white",
    requireName: false,
  },
  delete_permanent: {
    title: "Delete permanently",
    body: "This DESTROYS the community, all its posts, and all its replies. This action cannot be undone. The audit log is preserved.",
    cta: "Delete permanently",
    ctaClass: "bg-rose-600 hover:bg-rose-700 text-white",
    requireName: true,
  },
};

const CrewModerationModal = ({ open, onClose, crew, mode = "archive", onDone }) => {
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const cfg = MODES[mode] || MODES.archive;

  useEffect(() => {
    if (open) { setName(""); setReason(""); }
  }, [open, mode]);

  const disabled = busy || (cfg.requireName && (name || "").trim() !== (crew?.name || "").trim());

  const doAction = async () => {
    if (!crew?.crew_id) return;
    setBusy(true);
    try {
      let res;
      if (mode === "archive") {
        res = await api.delete(`/crews/${crew.crew_id}`, { params: { reason: reason || undefined } });
      } else if (mode === "restore") {
        res = await api.post(`/crews/${crew.crew_id}/restore`, null, { params: { reason: reason || undefined } });
      } else if (mode === "delete_permanent") {
        res = await api.delete(`/crews/${crew.crew_id}/permanent`, {
          params: { confirm_name: name.trim(), reason: reason || undefined },
        });
      }
      toast.success(res?.data?.message || cfg.cta);
      if (onDone) onDone(mode);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${cfg.cta.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  if (!crew) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent
        className="bg-gray-950 border border-white/10 text-white max-w-md"
        data-testid={`crew-moderation-modal-${mode}`}
      >
        <DialogHeader>
          <DialogTitle className="text-white">{cfg.title}</DialogTitle>
          <DialogDescription className="text-sm text-gray-400">
            {cfg.body}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Community</div>
            <div className="font-semibold text-white truncate" data-testid="crew-moderation-name">{crew.name}</div>
            <div className="text-[11px] text-gray-400">{crew.kind || "crew"} · {crew.visibility || "public"}</div>
          </div>

          {cfg.requireName && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-rose-300 font-bold">
                Type the community name to confirm
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 120))}
                placeholder={crew.name}
                autoFocus
                data-testid="crew-moderation-confirm-name"
                className="mt-1 bg-black/40 border-rose-500/40 text-white placeholder:text-gray-500"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Case sensitive — must match exactly.
              </p>
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
              Reason (optional, saved to audit log)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g. abandoned by owner, TOS violation, cleanup…"
              rows={2}
              maxLength={500}
              data-testid="crew-moderation-reason"
              className="mt-1 bg-black/40 border-white/10 text-white placeholder:text-gray-600"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="crew-moderation-cancel"
            className="text-gray-300 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={doAction}
            disabled={disabled}
            data-testid={`crew-moderation-confirm-${mode}`}
            className={cfg.ctaClass}
          >
            {busy ? "Working…" : cfg.cta}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CrewModerationModal;
